import {
  Box,
  Button,
  Divider,
  Flex,
  Text,
  useToast,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Icon,
  useDisclosure,
} from '@chakra-ui/react';
import { Select, SingleValue } from 'chakra-react-select';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useState } from 'react';
import { djs } from '@duke-hq/djs';
import { Eye } from 'lucide-react';

import { SlideKind, StudentFileResourceKind } from '@duke-hq/svc-api-db';
import { AssessmentFormRenderer } from '@duke-hq/ui/components/AssessmentForm/AssessmentFormRenderer';
import { useAssessmentFormWithAnswers } from '@duke-hq/ui/components/AssessmentForm/useAssessmentFormWithAnswers';
import { EditorHTML } from '@duke-hq/ui/components/DukeTiptapEditor/EditorHTML';
import { SubmittedFile } from '@duke-hq/ui/components/FileUpload/SubmittedFile';
import { useFlag } from '@duke-hq/flags/react';
import { hasTiptapContent } from '@duke-hq/lib/dukeTiptapEditorHelpers';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import type { AssessmentSubmissionWithAnswers } from '@duke-hq/svc-api/lib/assessment/types';
import type { FormattedSlide } from '../components/types';
import { testIds } from './testIds';
import { nth } from '@duke-hq/lib';

type PreviousAssessmentSubmission =
  RouterOutputs['assessments']['getById']['previousAssessmentSubmissions'][number];

function PreviousRecord({
  lastAnswerId,
  lastAnswerText,
  lastAnswerFiles,
  unitAssessmentEnrolmentId,
  assessedBy,
  optionFeedback,
  slide,
  previousAttempt,
}: {
  lastAnswerId: number;
  lastAnswerText: string | undefined;
  unitAssessmentEnrolmentId: string;
  assessedBy: string;
  optionFeedback?: string;
  lastAnswerFiles?: AssessmentSubmissionWithAnswers['files'];
  slide: FormattedSlide;
  previousAttempt: AssessmentSubmissionWithAnswers;
}) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: answerFeedback } = trpcAdmin.assessments.getAnswerFeedbackById.useQuery({
    answerId: lastAnswerId,
    unitAssessmentEnrolmentId,
  });

  const getSignedDownloadUrl = trpcAdmin.assessments.getSubmissionFileDownloadUrl.useMutation();

  const canPlayMedia = useFlag('ASSESSMENT_MEDIA_RECORDING_RELEASE') === true;

  const getFileUrl = async (fileId: string) => {
    const { signedUrl } = await getSignedDownloadUrl.mutateAsync({
      fileId,
      resourceId: unitAssessmentEnrolmentId,
      resourceKind: StudentFileResourceKind.ASSESSMENT_SLIDE_ANSWER,
    });
    return signedUrl;
  };

  const downloadFile = async (fileId: string) => {
    try {
      window.open(await getFileUrl(fileId), '_blank');
    } catch (error) {
      toast({
        title: `Error`,
        description:
          typeof error === 'object' &&
          error !== null &&
          'message' in error &&
          typeof error.message === 'string'
            ? error.message
            : t`There was an error downloading your file. Please try again. If the issue persists, please contact support.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box my={3}>
      <VStack gap={2.5} alignItems="start">
        {slide.kind === SlideKind.FORM ? (
          <>
            <Button onClick={onOpen} leftIcon={<Icon as={Eye} />} size="sm" variant="outline">
              <Trans>View attempt</Trans>
            </Button>
            <PreviousFormSubmissionModal
              isOpen={isOpen}
              onClose={onClose}
              previousAttempt={previousAttempt}
              slide={slide}
            />
          </>
        ) : (
          <>
            <Text fontSize="sm" fontWeight="medium">
              Response:
            </Text>
            {lastAnswerFiles?.map((file) => (
              <SubmittedFile
                key={file.submissionFileId}
                size="sm"
                filename={file.filename}
                mimetype={file.mimetype}
                sizeInBytes={file.sizeInBytes}
                onDownload={() => downloadFile(file.submissionFileId)}
                getPlaybackUrl={canPlayMedia ? () => getFileUrl(file.submissionFileId) : undefined}
              />
            ))}
            <Text fontSize="sm">{lastAnswerText}</Text>
          </>
        )}
      </VStack>
      <VStack gap={2.5} alignItems="start" mt={4}>
        <Flex justifyContent="space-between" w="100%">
          <Text fontSize="sm" fontWeight="medium">
            Feedback:
          </Text>
          <Text fontSize="sm" as="i">
            {assessedBy}
          </Text>
        </Flex>
        <Text fontSize="sm">{answerFeedback?.feedback || optionFeedback}</Text>
      </VStack>
    </Box>
  );
}

type PreviousFormSubmissionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  previousAttempt: AssessmentSubmissionWithAnswers;
  slide: FormattedSlide;
};

function PreviousFormSubmissionModal({
  isOpen,
  onClose,
  previousAttempt,
  slide,
}: PreviousFormSubmissionModalProps) {
  const { formSlideState, answerMap } = useAssessmentFormWithAnswers(
    previousAttempt.formFieldAnswers.map((f) => ({
      fieldId: f.fieldId,
      textInputValue: f.textInputValue ?? undefined,
      selectedOptions: f.selectedOptions,
      additionalInput: f.additionalInput ?? undefined,
      correct: f.correct,
    })),
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="780px">
        <ModalHeader>{slide.title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {hasTiptapContent(slide.question?.description) ? (
            <EditorHTML content={slide.question.description} style={{ padding: 0 }} />
          ) : null}

          <AssessmentFormRenderer
            slide={{ id: slide.slideId, sections: slide.sections ?? [] }}
            formSlideState={formSlideState}
            isReadOnly={true}
            isReviewResults={true}
            answerMap={answerMap}
            viewMode="mobile"
          />
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>
            <Trans>Close</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function PreviousAttempts({
  questionId,
  previousAssessmentSubmissions,
  options,
  unitAssessmentEnrolmentId,
  slide,
}: {
  questionId: string;
  previousAssessmentSubmissions: PreviousAssessmentSubmission[];
  options?: {
    id: string;
    answer: string;
    feedback?: string;
  }[];
  unitAssessmentEnrolmentId: string;
  slide: FormattedSlide;
}) {
  const [assessmentSubmissionId, setAssessmentSubmissionId] = useState<number | undefined>(
    previousAssessmentSubmissions[0]?.id,
  );

  const previousSubmissions = previousAssessmentSubmissions.filter((submission) =>
    submission.content.some((content) => content.questionId === questionId),
  );

  const previousSubmissionOptions = previousSubmissions.map((submission, index) => ({
    value: submission.id,
    label: `Previous Attempt (${nth(previousSubmissions?.length - index)})`,
  }));

  const lastSubmission = previousSubmissions.find(
    (submission) => submission.id === assessmentSubmissionId,
  );

  if (!lastSubmission?.content?.length) {
    return null;
  }

  const previousAttempt = lastSubmission.content.find(
    (content) => content.questionId === questionId,
  );

  if (!previousAttempt) {
    return null;
  }

  const assessedBy = `${lastSubmission.assessedBy?.first_name ?? ''} ${lastSubmission.assessedBy?.last_name ?? ''}`;

  const selectedOption = options?.find((option) => option.id === previousAttempt.answerId[0]);

  const answerText = previousAttempt.userInput || selectedOption?.answer;
  const optionFeedback = selectedOption?.feedback;

  return (
    <Box data-testid={testIds.previousAttemptsTable} py={4} px={7}>
      <Divider mb={4} />
      <Flex justifyContent="space-between" alignItems="center">
        {previousSubmissions.length > 1 ? (
          <Select
            options={previousSubmissionOptions}
            onChange={(newValue: SingleValue<{ value: number }>) =>
              setAssessmentSubmissionId(newValue?.value)
            }
            value={previousSubmissionOptions.find(
              (option) => option.value === assessmentSubmissionId,
            )}
            isSearchable={false}
          />
        ) : (
          <Text>Previous Attempt (1st)</Text>
        )}
        <Text>{djs(lastSubmission.submitted_at).format('DD/MM/YY')}</Text>
      </Flex>
      <PreviousRecord
        lastAnswerId={previousAttempt.originalAnswerId}
        lastAnswerText={answerText}
        unitAssessmentEnrolmentId={unitAssessmentEnrolmentId}
        assessedBy={assessedBy}
        optionFeedback={optionFeedback}
        lastAnswerFiles={previousAttempt.files}
        slide={slide}
        previousAttempt={previousAttempt}
      />
    </Box>
  );
}

export default PreviousAttempts;
